import { useEffect, useState } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'
import { ArrowLeft, Share2 } from 'lucide-react'
import { useRouter } from '@tanstack/react-router'
import { useTelegram } from '@/shared/lib/telegram'
import { cn } from '@/shared/lib/utils'

export interface EventFloatingHeaderProps {
  /**
   * Название события
   */
  eventName: string
  /**
   * ID события — нужен для построения корректной share-ссылки на миниапп
   */
  eventId?: string
  /**
   * Показывать ли header (для анимации)
   */
  show?: boolean
}

/**
 * Виджет: Floating Header для страницы события
 * 
 * Минималистичный header, который появляется при скролле.
 * Прозрачный поверх галереи, становится opaque при скролле вниз.
 */
export function EventFloatingHeader({
  eventName,
  eventId,
  show = true,
}: EventFloatingHeaderProps) {
  const { hapticFeedback } = useTelegram()
  const { scrollY } = useScroll()
  const [isScrolled, setIsScrolled] = useState(false)
  const router = useRouter()
  
  // Определяем, прокручен ли экран
  useEffect(() => {
    const unsubscribe = scrollY.on('change', (latest) => {
      setIsScrolled(latest > 100)
    })
    return unsubscribe
  }, [scrollY])
  
  // Анимация прозрачности и фона
  const headerOpacity = useTransform(scrollY, [0, 100], [show ? 1 : 0, 1])
  const headerBgOpacity = useTransform(scrollY, [0, 100], [0, 1])
  const titleOpacity = useTransform(scrollY, [0, 100], [0, 1])
  
  const handleShare = () => {
    hapticFeedback.impactOccurred('light')
    
    // Direct-link to the mini-app on the event screen — sharing window.location.href
    // would leak the in-app URL which doesn't open in Telegram for receivers.
    const eventLink = eventId
      ? `https://t.me/drakonisoroka_bot/shop?startapp=event_${eventId}`
      : 'https://t.me/drakonisoroka_bot/shop?startapp=events'
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(eventLink)}&text=${encodeURIComponent(eventName)}`
    
    if (window.Telegram?.WebApp?.openTelegramLink) {
      window.Telegram.WebApp.openTelegramLink(shareUrl)
    } else {
      window.open(shareUrl, '_blank')
    }
  }
  
  if (!show && !isScrolled) {
    return null
  }
  
  return (
    <motion.div
      style={{
        opacity: headerOpacity,
        paddingTop: 'calc(var(--top-inset, 0px) + 12px)',
      }}
      className={cn(
        'fixed top-0 left-0 right-0 z-30',
        'flex items-center justify-between px-4 lg:px-6 py-3',
        'transition-colors duration-150'
      )}
    >
      {/* Фон с blur */}
      <motion.div
        style={{
          opacity: headerBgOpacity,
        }}
        className="absolute inset-0 bg-paper/95 dark:bg-[#0F1113]/95 backdrop-blur-md"
        aria-hidden="true"
      />
      
      {/* Контент */}
      <div className="relative flex items-center justify-between w-full">
        {/* Кнопка назад */}
        <button
          type="button"
          aria-label="Назад"
          onClick={() => {
            hapticFeedback.impactOccurred('light')
            router.history.back()
          }}
          className={cn(
            'w-10 h-10 flex items-center justify-center rounded-full',
            'bg-white/80 dark:bg-dark-card/80 backdrop-blur-sm',
            'border border-silk/50 dark:border-dark-border/50',
            'text-gray-600 dark:text-gray-400',
            'active:scale-95 transition-all shadow-sm',
            !isScrolled && 'bg-black/20 dark:bg-white/10 border-transparent text-white',
          )}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        
        {/* Название события (показывается при скролле) */}
        <motion.div
          style={{
            opacity: titleOpacity,
          }}
          className="flex-1 px-4"
        >
          <h1 className="text-base font-semibold text-ink dark:text-white truncate text-center">
            {eventName}
          </h1>
        </motion.div>
        
        {/* Кнопка поделиться */}
        <button
          type="button"
          onClick={handleShare}
          className={cn(
            'w-10 h-10 flex items-center justify-center rounded-full',
            'bg-white/80 dark:bg-dark-card/80 backdrop-blur-sm',
            'border border-silk/50 dark:border-dark-border/50',
            'text-gray-600 dark:text-gray-400',
            'active:scale-95 transition-all shadow-sm',
            !isScrolled && 'bg-black/20 dark:bg-white/10 border-transparent text-white',
          )}
          aria-label="Поделиться"
        >
          <Share2 className="h-5 w-5" />
        </button>
      </div>
    </motion.div>
  )
}




